import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";

/**
 * Единый Modal/Drawer платформы. Canon: bottom-sheet на мобильном,
 * центр на desktop (variant="sheet"); variant="center" — всегда центр.
 * Засечек (border-l) нет; глубина — shadow-drawer.
 *
 * Заменяет копипасту fixed inset-0 z-50 в модалках страниц.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  eyebrow,
  children,
  variant = "sheet",
  testId,
  maxWidth = "md:max-w-2xl",
}: {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** mono-надзаголовок над title (например «МЕТРИКА · РАЗБОР») */
  eyebrow?: string;
  children: React.ReactNode;
  /** sheet: снизу на мобиле, центр на md+. center: всегда по центру */
  variant?: "sheet" | "center";
  testId?: string;
  maxWidth?: string;
}) {
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const placement =
    variant === "center"
      ? "items-center"
      : "items-end md:items-center";
  const shape =
    variant === "center" ? "rounded-card" : "rounded-t-card md:rounded-card";

  // Портал в body: иначе `fixed inset-0` ловится в containing-block любого
  // предка с transform (например `main > *` с анимацией page-enter оставляет
  // identity-transform) — на длинных страницах (Аналитика) модалка
  // позиционировалась относительно страницы, а не вьюпорта, и рендерилась криво.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      className={`fixed inset-0 z-50 flex ${placement} justify-center bg-bg-base/70 backdrop-blur-sm animate-backdrop-in`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`w-full ${maxWidth} ${shape} bg-bg-card shadow-drawer max-h-[85vh] overflow-y-auto animate-sheet-in`}
      >
        {(title || eyebrow) && (
          <header className="sticky top-0 z-10 bg-bg-card border-b border-bg-border px-5 py-3 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              {eyebrow && (
                <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
                  {eyebrow}
                </div>
              )}
              {title && (
                <h2 className="text-lg font-bold text-fg-primary mt-0.5 truncate">
                  {title}
                </h2>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("a11y.close", { defaultValue: "Закрыть" })}
              className="text-fg-muted hover:text-fg-primary shrink-0"
            >
              <X size={20} aria-hidden />
            </button>
          </header>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
